import {
  useSimilarRecords,
  type SimilarReason,
} from "@/core/common/hooks/useSimilarRecords";
import {
  isSimilarName,
  normalizeCode,
  searchTermsFor,
} from "@/core/common/utils/similarity";
import type { SimilarRecordItem } from "@/ui/SimilarRecordsHint";
import type { WorkOrder } from "../type/WorkOrder";
import { getWorkOrder } from "./useGetWorkOrder";

interface SimilarWorkOrdersInput {
  name?: string;
  code?: string;
  zoneId?: string;
}

export const useSimilarWorkOrders = ({
  name = "",
  code = "",
  zoneId,
}: SimilarWorkOrdersInput): SimilarRecordItem[] => {
  const normalizedCode = normalizeCode(code);

  const { matches } = useSimilarRecords<WorkOrder>({
    key: `workOrders:${zoneId}`,
    terms: [
      ...searchTermsFor(name),
      ...(normalizedCode.length >= 2 ? [code.trim()] : []),
    ],
    fetchCandidates: async (term) =>
      (await getWorkOrder(zoneId, 1, 20, true, term)).data,
    getId: (workOrder) => workOrder.id,
    match: (workOrder) => {
      const reasons: SimilarReason[] = [];
      if (normalizedCode && normalizeCode(workOrder.code) === normalizedCode)
        reasons.push("code");
      if (isSimilarName(name, workOrder.name)) reasons.push("name");
      return reasons;
    },
    enabled: !!zoneId,
  });

  return matches.map(({ item, reasons }) => ({
    id: item.id,
    title: `${item.code} · ${item.name}`,
    subtitle: item.clientEntity?.name,
    reasons,
    enabled: item.enabled,
  }));
};
